/**
 * Admin → Users — typed api-client calls + TanStack Query hooks
 * (E05-F01-T03 + E05-F01-T04).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). The transport uses
 * the shared `apiClient` (auto-injects the session Bearer token, normalizes the
 * response, never throws) and `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` so the SPA speaks the exact same
 * shapes as the Fastify API.
 *
 * Endpoints (1:1 with the legacy n8n `manage_users` action router — get_users /
 * create_user / update_user / delete_user / disconnect_outlook,
 * docs/source-n8n/reference/Users.md):
 *   GET    /admin/users                          → useUsers()            (get_users)
 *   GET    /admin/users/:id/email-signature       → useUserSignature()    (E10-F04-T10)
 *   POST   /admin/users                          → useCreateUser()       (create_user)
 *   PATCH  /admin/users/:id                       → useUpdateUser()       (update_user)
 *   DELETE /admin/users/:id                       → useDeleteUser()       (delete_user)
 *   POST   /admin/users/:id/disconnect-outlook    → useDisconnectOutlook()(disconnect_outlook)
 *
 * The legacy SPA POSTed `{ command:'manage_users', action, ... }` to one webhook;
 * the v2 transport is clean REST but the `command:'manage_users'` tag is still
 * passed for the legacy-success unwrap to mirror the other admin tabs. The list is
 * PASSWORD-FREE (the API strips every secret — T01 #1 / T02 #3); the SPA never
 * sees a password (the e2e asserts no password leaks into the DOM).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  adminUsersListResponseSchema,
  adminUserMutationResponseSchema,
  adminUserDeleteResponseSchema,
  adminUserDisconnectOutlookResponseSchema,
  adminUserSignatureResponseSchema,
  type AdminUserDTO,
  type CreateAdminUserInput,
  type UpdateAdminUserInput,
  type AdminUserMutationResponse,
  type AdminUserDeleteResponse,
  type AdminUserDisconnectOutlookResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query key (kept out of the shared registry). */
export const USERS_QUERY_KEY = ['admin', 'users'] as const;

/** The legacy command tag carried on every users request (manage_users router). */
const USERS_COMMAND = { command: 'manage_users' } as const;

/** Fetch every user (password-free; server orders created_at ASC). */
async function fetchUsers(): Promise<AdminUserDTO[]> {
  const result = await apiClient.get<unknown>('/admin/users', USERS_COMMAND);
  const body = adminUsersListResponseSchema.parse(unwrap(result));
  return body.users;
}

/**
 * Fetch ONE user's saved signature HTML (E10-F04-T10). Separate from the list
 * because the value carries the logo as a `data:` URI (~256 KB) and only the open
 * Edit dialog can show it — see the contract note on
 * `adminUserSignatureResponseSchema`.
 */
async function fetchUserSignature(id: string): Promise<string> {
  const result = await apiClient.get<unknown>(
    `/admin/users/${encodeURIComponent(id)}/email-signature`,
    USERS_COMMAND,
  );
  return adminUserSignatureResponseSchema.parse(unwrap(result)).email_signature;
}

/** Create a user (name+email required; argon2 password server-side). */
async function postUser(input: CreateAdminUserInput): Promise<AdminUserMutationResponse> {
  const result = await apiClient.post<unknown>('/admin/users', input, USERS_COMMAND);
  return adminUserMutationResponseSchema.parse(unwrap(result));
}

/** Partial-update a user by id (empty password keeps the stored hash). */
async function patchUser(args: {
  id: string;
  patch: UpdateAdminUserInput;
}): Promise<AdminUserMutationResponse> {
  const result = await apiClient.patch<unknown>(
    `/admin/users/${encodeURIComponent(args.id)}`,
    args.patch,
    USERS_COMMAND,
  );
  return adminUserMutationResponseSchema.parse(unwrap(result));
}

/** Delete a user by id (legacy parity: always answers success). */
async function deleteUserReq(id: string): Promise<AdminUserDeleteResponse> {
  const result = await apiClient.delete<unknown>(
    `/admin/users/${encodeURIComponent(id)}`,
    USERS_COMMAND,
  );
  return adminUserDeleteResponseSchema.parse(unwrap(result));
}

/** Soft-disconnect a user's Outlook (tokens kept; legacy disconnect_outlook). */
async function disconnectOutlookReq(id: string): Promise<AdminUserDisconnectOutlookResponse> {
  const result = await apiClient.post<unknown>(
    `/admin/users/${encodeURIComponent(id)}/disconnect-outlook`,
    undefined,
    USERS_COMMAND,
  );
  return adminUserDisconnectOutlookResponseSchema.parse(unwrap(result));
}

/** List query — the Users table. */
export function useUsers(): UseQueryResult<AdminUserDTO[], ApiResultError> {
  return useQuery<AdminUserDTO[], ApiResultError>({
    queryKey: USERS_QUERY_KEY,
    queryFn: fetchUsers,
  });
}

/**
 * The signature of one user — loaded when the Edit dialog opens (E10-F04-T10).
 * `id === null` (Add dialog / closed) disables the query. The key sits UNDER
 * `USERS_QUERY_KEY`, so the existing prefix invalidation after a save refreshes the
 * signature too.
 */
export function useUserSignature(id: string | null): UseQueryResult<string, ApiResultError> {
  return useQuery<string, ApiResultError>({
    queryKey: [...USERS_QUERY_KEY, id ?? '', 'signature'],
    queryFn: () => fetchUserSignature(id!),
    enabled: id != null && id.length > 0,
  });
}

/** Create mutation — invalidates the list so the new user appears. */
export function useCreateUser(): UseMutationResult<
  AdminUserMutationResponse,
  ApiResultError,
  CreateAdminUserInput
> {
  const queryClient = useQueryClient();
  return useMutation<AdminUserMutationResponse, ApiResultError, CreateAdminUserInput>({
    mutationFn: postUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}

/** Update mutation — invalidates the list so the edit re-renders. */
export function useUpdateUser(): UseMutationResult<
  AdminUserMutationResponse,
  ApiResultError,
  { id: string; patch: UpdateAdminUserInput }
> {
  const queryClient = useQueryClient();
  return useMutation<
    AdminUserMutationResponse,
    ApiResultError,
    { id: string; patch: UpdateAdminUserInput }
  >({
    mutationFn: patchUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}

/** Delete mutation — invalidates the list so the removed row disappears. */
export function useDeleteUser(): UseMutationResult<
  AdminUserDeleteResponse,
  ApiResultError,
  string
> {
  const queryClient = useQueryClient();
  return useMutation<AdminUserDeleteResponse, ApiResultError, string>({
    mutationFn: deleteUserReq,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}

/** Disconnect-Outlook mutation — invalidates the list so the badge updates. */
export function useDisconnectOutlook(): UseMutationResult<
  AdminUserDisconnectOutlookResponse,
  ApiResultError,
  string
> {
  const queryClient = useQueryClient();
  return useMutation<AdminUserDisconnectOutlookResponse, ApiResultError, string>({
    mutationFn: disconnectOutlookReq,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}
